import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  readonly mainDashboardPath = '/dashboard';
  dashboardLoading = false;

  // KPI card values – populated from API only
  kpiTotalProposals = 0;
  kpiTotalContracts = 0;
  kpiOnGoing = 0;
  kpiCompleted = 0;
  kpiShortClosed = 0;
  kpiAspoApproved = 0;
  kpiPilIndigenised = 0;
  kpiPilUnderIndigenisation = 0;
  kpiCfiTotal = 0;

  kpiSplitScheme = { revenue: 0, idex: 0, make: 0, tdf: 0 };
  kpiSplitContracts = { revenue: 0, idex: 0, make: 0, tdf: 0 };
  kpiSplitActive = { revenue: 0, idex: 0, make: 0, tdf: 0 };
  kpiSplitCompleted = { revenue: 0, idex: 0, make: 0, tdf: 0 };
  kpiSplitShortClosed = { revenue: 0, idex: 0, make: 0, tdf: 0 };
  kpiSplitApso = { revenue: 0, idex: 0, make: 0, tdf: 0 };
  kpiCfiSplit = { pending: 0, inReview: 0, closed: 0 };

  schemeSummaryRows: any[] = [];
  pilCfiSummaryRows: any[] = [];

  // Chart 1: Scheme-wise Proposal Distribution (grouped bar)
  chart1Data: any;
  chart1Options: any;

  // Chart 2: Proposal Status Overview (stacked bar)
  chart2Data: any;
  chart2Options: any;

  // Chart 3: Scheme Lifecycle Progress (pie charts per scheme)
  chart3SchemeMetrics: any[] = [];
  chart3PieOptions: any;

  // Chart 4: PIL Indigenisation Status (doughnut)
  chart4Data: any;
  chart4Options: any;

  // Chart 5: Year-wise PIL Trend (line/bar)
  chart5Data: any;
  chart5Options: any;

  // Chart 6: CFI Request Status (doughnut)
  chart6Data: any;
  chart6Options: any;

  // Chart 7: CFI Request Trend (line/bar)
  chart7Data: any;
  chart7Options: any;

  // Chart 8: Seek Proposal Analytics (two charts)
  chart8aData: any;
  chart8aOptions: any;
  chart8bData: any;
  chart8bOptions: any;

  // Chart 9: Execution / Closure Monitoring
  chart9Data: any;
  chart9Options: any;

  constructor(
    private router: Router,
    private api: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initCharts();
    this.loadTransactionDashboard();
    // this.loadSeekProposalCharts();
  }

  /** Coerce API value to number: supports number or object with .count. Never returns NaN. */
  private toKpiNumber(val: any, fallback: number): number {
    if (val == null || val === '') return fallback;
    if (typeof val === 'number' && !Number.isNaN(val)) return val;
    if (typeof val === 'object' && typeof val.count === 'number') return val.count;
    const n = Number(val);
    return Number.isNaN(n) ? fallback : n;
  }

  /** Get scheme value from scheme_split; handles API key variations (Make/MAKE, IDEX/idex, etc.) */
  private getSchemeVal(split: any, scheme: string): number {
    if (!split || typeof split !== 'object') return 0;
    const keys = scheme === 'MAKE' ? ['MAKE', 'Make', 'make'] :
      scheme === 'Revenue' ? ['Revenue', 'revenue'] :
      scheme === 'TDF' ? ['TDF', 'tdf'] :
      scheme === 'IDEX' ? ['IDEX', 'iDEX', 'idex'] : [scheme];
    for (const k of keys) {
      const v = split[k];
      if (v !== undefined && v !== null) return this.toKpiNumber(v, 0);
    }
    return 0;
  }

  /** Get scheme object from dashboard payload (handles Make/MAKE, iDEX/IDEX, etc.) */
  private getSchemeObj(d: any, scheme: string): any {
    if (!d || typeof d !== 'object') return {};
    const keys =
      scheme === 'MAKE'
        ? ['MAKE', 'Make', 'make']
        : scheme === 'Revenue'
          ? ['Revenue', 'revenue']
          : scheme === 'TDF'
            ? ['TDF', 'tdf']
            : scheme === 'IDEX'
              ? ['IDEX', 'iDEX', 'idex']
              : [scheme];
    for (const k of keys) {
      const v = d[k];
      if (v && typeof v === 'object') return v;
    }
    return {};
  }

  /** Build split object { revenue, idex, make, tdf } from scheme_split */
  private buildSplitObj(split: any): { revenue: number; idex: number; make: number; tdf: number } {
    return {
      revenue: this.getSchemeVal(split, 'Revenue'),
      idex: this.getSchemeVal(split, 'IDEX'),
      make: this.getSchemeVal(split, 'MAKE'),
      tdf: this.getSchemeVal(split, 'TDF'),
    };
  }

  /** Load dashboard data from base_url/transaction/dashboard/
   * API keys: total_proposals, active_proposals, completed_proposals, short_closed, apso_approved,
   * total_contracts (each with count + scheme_split), Revenue/IDEX/MAKE/TDF (lifecycle metrics),
   * pil_indigenised, pil_under_indigenised, cfi_request
   */
  loadTransactionDashboard(): void {
    this.dashboardLoading = true;
    this.api.getAPI('/transaction/dashboard/').subscribe({
      next: (res: any) => {
        // Use API payload: support res.data, res.results, or raw res
        const d = res?.data ?? res?.results ?? res;
        if (!d || typeof d !== 'object') {
          this.dashboardLoading = false;
          return;
        }
        // All KPIs and chart data below are from API (d) only; no static values
        const tp = d.total_proposals ?? d.totalProposals;
        const ap = d.active_proposals ?? d.activeProposals;
        const cp = d.completed_proposals ?? d.completedProposals;
        const sc = d.short_closed ?? d.shortClosed;
        const aa = d.apso_approved ?? d.apsoApproved;
        const tc = d.total_contracts ?? d.totalContracts;

        this.kpiTotalProposals = this.toKpiNumber(tp, 0);
        this.kpiTotalContracts = this.toKpiNumber(tc, 0);
        this.kpiOnGoing = this.toKpiNumber(ap, 0);
        this.kpiCompleted = this.toKpiNumber(cp, 0);
        this.kpiShortClosed = this.toKpiNumber(sc, 0);
        this.kpiAspoApproved = this.toKpiNumber(aa, 0);
        this.kpiPilIndigenised = this.toKpiNumber(d.pil_indigenised ?? d.pilIndigenised, 0);
        this.kpiPilUnderIndigenisation = this.toKpiNumber(d.pil_under_indigenised ?? d.pilUnderIndigenisation, 0);
        const cfiReq = d.cfi_request ?? d.cfiRequest ?? {};
        // API shape (per response): { total, pending, in_progress, completed }
        this.kpiCfiTotal = this.toKpiNumber(
          cfiReq?.total ?? cfiReq?.count ?? d.cfi_total ?? d.cfiTotal,
          0
        );

        const proposalSplit = tp?.scheme_split ?? tp?.schemeSplit ?? {};
        const contractSplit = tc?.scheme_split ?? tc?.schemeSplit ?? {};
        const activeSplit = ap?.scheme_split ?? ap?.schemeSplit ?? {};
        const completedSplit = cp?.scheme_split ?? cp?.schemeSplit ?? {};
        const shortSplit = sc?.scheme_split ?? sc?.schemeSplit ?? {};
        const apsoSplit = aa?.scheme_split ?? aa?.schemeSplit ?? {};

        this.kpiSplitScheme = this.buildSplitObj(proposalSplit);
        this.kpiSplitContracts = this.buildSplitObj(contractSplit);
        this.kpiSplitActive = this.buildSplitObj(activeSplit);
        this.kpiSplitCompleted = this.buildSplitObj(completedSplit);
        this.kpiSplitShortClosed = this.buildSplitObj(shortSplit);
        this.kpiSplitApso = this.buildSplitObj(apsoSplit);

        const cfiSplit = d.cfi_split ?? d.cfiSplit ?? cfiReq ?? {};
        this.kpiCfiSplit = {
          pending: this.toKpiNumber(cfiSplit.pending, 0),
          inReview: this.toKpiNumber(
            cfiSplit.in_review ?? cfiSplit.inReview ?? cfiSplit.in_progress ?? cfiSplit.inProgress,
            0
          ),
          closed: this.toKpiNumber(
            cfiSplit.closed ?? cfiSplit.completed ?? cfiSplit.done,
            0
          ),
        };

        const schemeOrder = ['MAKE', 'Revenue', 'TDF', 'IDEX'];
        this.schemeSummaryRows = schemeOrder.map(name => ({
          scheme: name === 'IDEX' ? 'iDEX' : name,
          totalProposals: this.getSchemeVal(proposalSplit, name),
          onGoing: this.getSchemeVal(activeSplit, name),
          completed: this.getSchemeVal(completedSplit, name),
          shortClosed: this.getSchemeVal(shortSplit, name),
          contracts: this.getSchemeVal(contractSplit, name),
          lastUpdated: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        }));

        this.pilCfiSummaryRows = [
          { module: 'PIL', totalItems: this.kpiPilIndigenised + this.kpiPilUnderIndigenisation, inProgress: this.kpiPilUnderIndigenisation, completedClosed: this.kpiPilIndigenised, lastUpdated: '—' },
          { module: 'CFI', totalItems: this.kpiCfiTotal, inProgress: this.kpiCfiSplit.inReview, completedClosed: this.kpiCfiSplit.closed, lastUpdated: '—' },
        ];

        this.chart3SchemeMetrics = schemeOrder.map(name => {
          const obj = this.getSchemeObj(d, name);
      return {
            scheme: name === 'IDEX' ? 'iDEX' : name,
            initiated: this.toKpiNumber(obj.initiated, 0),
            approved: this.toKpiNumber(obj.apso_approved ?? obj.apsoApproved, 0),
            contracted: this.toKpiNumber(obj.contracted, 0),
            execution: this.toKpiNumber(obj.ongoing ?? obj.execution, 0),
            closed: this.toKpiNumber(obj.completed ?? obj.closed, 0),
      };
    });

        const getSchemeArr = (m: any) => [
          this.getSchemeVal(m?.scheme_split ?? m?.schemeSplit ?? {}, 'MAKE'),
          this.getSchemeVal(m?.scheme_split ?? m?.schemeSplit ?? {}, 'Revenue'),
          this.getSchemeVal(m?.scheme_split ?? m?.schemeSplit ?? {}, 'TDF'),
          this.getSchemeVal(m?.scheme_split ?? m?.schemeSplit ?? {}, 'IDEX'),
        ];
        const totalArr = getSchemeArr(tp);
        const activeArr = getSchemeArr(ap);
        const completedArr = getSchemeArr(cp);
        const shortArr = getSchemeArr(sc);
        this.chart1Data = {
          labels: ['MAKE', 'Revenue', 'TDF', 'iDEX'],
          datasets: [
            { label: 'Total Proposals', backgroundColor: '#3B82F6', data: totalArr },
            { label: 'On-Going', backgroundColor: '#06B6D4', data: activeArr },
            { label: 'Completed', backgroundColor: '#22C55E', data: completedArr },
            { label: 'Short Closed', backgroundColor: '#94A3B8', data: shortArr },
          ],
        };

        const pendingArr = totalArr.map((t, i) => Math.max(0, t - (activeArr[i] ?? 0) - (completedArr[i] ?? 0) - (shortArr[i] ?? 0)));
        this.chart2Data = {
          labels: ['MAKE', 'Revenue', 'TDF', 'iDEX'],
          datasets: [
            { label: 'Pending', backgroundColor: '#F59E0B', data: pendingArr, stack: 'stack0' },
            { label: 'In Progress', backgroundColor: '#38BDF8', data: activeArr, stack: 'stack0' },
            { label: 'Completed', backgroundColor: '#22C55E', data: completedArr, stack: 'stack0' },
            { label: 'Short Closed', backgroundColor: '#64748B', data: shortArr, stack: 'stack0' },
          ],
        };

        const execSchemeNames = schemeOrder;
        const execLabels = execSchemeNames.map(n => n === 'IDEX' ? 'iDEX' : n);
        this.chart9Data = {
          labels: execLabels,
          datasets: [
            {
              label: 'Pre-Contract',
              backgroundColor: '#94A3B8',
              data: execSchemeNames.map(n => {
                const o = this.getSchemeObj(d, n);
                return this.toKpiNumber(o.initiated, 0) + this.toKpiNumber(o.apso_approved ?? o.apsoApproved, 0);
              }),
              stack: 'stack0'
            },
            { label: 'Contracted', backgroundColor: '#3B82F6', data: execSchemeNames.map(n => this.toKpiNumber(this.getSchemeObj(d, n).contracted, 0)), stack: 'stack0' },
            { label: 'Execution', backgroundColor: '#06B6D4', data: execSchemeNames.map(n => this.toKpiNumber(this.getSchemeObj(d, n).ongoing ?? this.getSchemeObj(d, n).execution, 0)), stack: 'stack0' },
            { label: 'Trials', backgroundColor: '#F59E0B', data: execSchemeNames.map(() => 0), stack: 'stack0' },
            { label: 'Closure', backgroundColor: '#22C55E', data: execSchemeNames.map(n => this.toKpiNumber(this.getSchemeObj(d, n).completed ?? this.getSchemeObj(d, n).closed, 0)), stack: 'stack0' },
          ],
        };

        this.chart4Data = {
          labels: ['Indigenised as on Date', 'Under Indigenisation'],
          datasets: [{ data: [this.kpiPilIndigenised, this.kpiPilUnderIndigenisation], backgroundColor: ['#22C55E', '#F59E0B'], hoverOffset: 4 }]
        };
        this.chart6Data = {
          labels: ['Pending', 'In Review', 'Closed'],
          datasets: [{ data: [this.kpiCfiSplit.pending, this.kpiCfiSplit.inReview, this.kpiCfiSplit.closed], backgroundColor: ['#F59E0B', '#3B82F6', '#22C55E'], hoverOffset: 4 }]
        };

        this.dashboardLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading transaction dashboard:', err);
        this.dashboardLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  getLifecyclePieData(m: { initiated: number; approved: number; contracted: number; execution: number; closed: number }): any {
    const labels = ['Initiated', 'APSO Approved', 'Contracted', 'Execution', 'Closed'];
    const safe = (v: any) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const data = [safe(m.initiated), safe(m.approved), safe(m.contracted), safe(m.execution), safe(m.closed)];
    const colors = ['#3B82F6', '#06B6D4', '#8B5CF6', '#F59E0B', '#22C55E'];
    return {
      labels,
      datasets: [{ data, backgroundColor: colors, hoverOffset: 6 }]
    };
  }

  private getLifecyclePieOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      layout: { padding: 8 },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: any) => `${context.label}: ${context.parsed}`
          }
        }
      }
    };
  }

  private initCharts(): void {
    const schemeLabels = ['MAKE', 'Revenue', 'TDF', 'iDEX'];
    const emptyData = [0, 0, 0, 0];

    this.chart3PieOptions = this.getLifecyclePieOptions();
    this.chart1Data = {
      labels: schemeLabels,
      datasets: [
        { label: 'Total Proposals', backgroundColor: '#3B82F6', data: [...emptyData] },
        { label: 'On-Going', backgroundColor: '#06B6D4', data: [...emptyData] },
        { label: 'Completed', backgroundColor: '#22C55E', data: [...emptyData] },
        { label: 'Short Closed', backgroundColor: '#94A3B8', data: [...emptyData] },
      ]
    };
    this.chart1Options = this.getGroupedBarOptions();
    this.chart2Data = {
      labels: schemeLabels,
      datasets: [
        { label: 'Pending', backgroundColor: '#F59E0B', data: [...emptyData], stack: 'stack0' },
        { label: 'In Progress', backgroundColor: '#38BDF8', data: [...emptyData], stack: 'stack0' },
        { label: 'Completed', backgroundColor: '#22C55E', data: [...emptyData], stack: 'stack0' },
        { label: 'Short Closed', backgroundColor: '#64748B', data: [...emptyData], stack: 'stack0' },
      ]
    };
    this.chart2Options = this.getStackedBarOptions();
    this.chart4Data = {
      labels: ['Indigenised as on Date', 'Under Indigenisation'],
      datasets: [{ data: [0, 0], backgroundColor: ['#22C55E', '#F59E0B'], hoverOffset: 4 }]
    };
    this.chart4Options = { responsive: true, maintainAspectRatio: false, cutout: '65%' };
    this.chart5Data = {
      labels: [],
      datasets: [
        { label: 'Newly Indigenised', borderColor: '#22C55E', fill: false, tension: 0.3, data: [] },
        { label: 'Under Progress', borderColor: '#F59E0B', fill: false, tension: 0.3, data: [] }
      ]
    };
    this.chart5Options = this.getLineChartOptions();
    this.chart6Data = {
      labels: ['Pending', 'In Review', 'Closed'],
      datasets: [{ data: [0, 0, 0], backgroundColor: ['#F59E0B', '#3B82F6', '#22C55E'], hoverOffset: 4 }]
    };
    this.chart6Options = { responsive: true, maintainAspectRatio: false, cutout: '65%' };
    this.chart7Data = {
      labels: [],
      datasets: [
        { label: 'Total Raised', borderColor: '#3B82F6', fill: false, tension: 0.3, data: [] },
        { label: 'Closed', borderColor: '#22C55E', fill: false, tension: 0.3, data: [] },
        { label: 'Pending', borderColor: '#F59E0B', fill: false, tension: 0.3, data: [] }
      ]
    };
    this.chart7Options = this.getLineChartOptions();
    this.chart8aData = {
      labels: ['Proposal Submitted', 'Nil Return', 'No Response'],
      datasets: [{ data: [0, 0, 0], backgroundColor: ['#22C55E', '#F59E0B', '#94A3B8'], hoverOffset: 4 }]
    };
    this.chart8aOptions = this.getChart8aDoughnutOptions();
    this.chart8bData = {
      labels: ['MAKE', 'TDF'],
      datasets: [
        {
          label: 'Proposals generated',
          backgroundColor: '#3B82F6',
          data: [0, 0],
        },
      ],
    };
    this.chart8bOptions = this.getGroupedBarOptions();
    this.chart9Data = {
      labels: schemeLabels,
      datasets: [
        { label: 'Pre-Contract', backgroundColor: '#94A3B8', data: [...emptyData], stack: 'stack0' },
        { label: 'Contracted', backgroundColor: '#3B82F6', data: [...emptyData], stack: 'stack0' },
        { label: 'Execution', backgroundColor: '#06B6D4', data: [...emptyData], stack: 'stack0' },
        { label: 'Trials', backgroundColor: '#F59E0B', data: [...emptyData], stack: 'stack0' },
        { label: 'Closure', backgroundColor: '#22C55E', data: [...emptyData], stack: 'stack0' },
      ]
    };
    this.chart9Options = this.getStackedBarOptions();
  }

  private getGroupedBarOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'top' } },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 45 } },
        y: { beginAtZero: true, ticks: { precision: 0 } }
      }
    };
  }

  private getStackedBarOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'top' } },
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } }
      }
    };
  }

  private getLineChartOptions(): any {
      return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'top' } },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 } }
      }
    };
  }

  goToDetails(kpiId: string): void {
    this.router.navigate(['/dashboard/details', 'kpi', kpiId]);
  }

  goToChartDetails(chartId: string): void {
    this.router.navigate(['/dashboard/details', 'chart', chartId]);
  }

  /** Doughnut: clear legend with counts + tooltips (MAKE+TDF seek proposals). */
  private getChart8aDoughnutOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '58%',
      layout: { padding: { top: 8, bottom: 8, left: 8, right: 8 } },
      plugins: {
        legend: {
          position: 'bottom' as const,
          align: 'center' as const,
          labels: {
            padding: 18,
            boxWidth: 16,
            boxHeight: 16,
            usePointStyle: true,
            pointStyle: 'rectRounded' as const,
            font: { size: 13, weight: '600' as const },
            color: '#334155',
            generateLabels: (chart: any) => {
              const ds = chart.data.datasets[0];
              const labels: string[] = chart.data.labels || [];
              const bg = ds?.backgroundColor;
              const data: number[] = ds?.data || [];
              return labels.map((label: string, i: number) => ({
                text: `${label}: ${data[i] ?? 0}`,
                fillStyle: Array.isArray(bg) ? bg[i] : bg,
                strokeStyle: Array.isArray(bg) ? bg[i] : bg,
                lineWidth: 0,
                hidden: false,
                index: i,
              }));
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const dataArr = (ctx.dataset?.data ?? []) as number[];
              const v = Number(dataArr[ctx.dataIndex]) || 0;
              const sum = dataArr.reduce((a, b) => a + (Number(b) || 0), 0);
              const pct = sum > 0 ? Math.round((v / sum) * 100) : 0;
              return ` ${ctx.label}: ${v} (${pct}%)`;
            },
          },
        },
      },
    };
  }

  /**
   * One collaborative load for charts 8a+8b: prefer a single GET with both schemes
   * (`scheme_id__in=47,16`). If that returns no usable rows, merge exactly two calls
   * for scheme_id 47 and 16 only (no Revenue/iDEX).
   */
  // private loadSeekProposalCharts(): void {
  //   this.fetchSeekProposalsMakeTdf()
  //     .pipe(
  //       map(({ make, tdf }) => {
  //         const agg = (rows: any[]) => {
  //           let ps = 0;
  //           let nr = 0;
  //           let no = 0;
  //           let gen = 0;
  //           for (const item of rows) {
  //             const m = this.metricsFromSeekProposalRow(item);
  //             ps += m.ps;
  //             nr += m.nr;
  //             no += m.no;
  //             gen += m.gen;
  //           }
  //           return { ps, nr, no, gen };
  //         };
  //         const m = agg(make);
  //         const t = agg(tdf);
  //         return {
  //           chart8a: [m.ps + t.ps, m.nr + t.nr, m.no + t.no] as const,
  //           chart8b: [m.gen, t.gen] as const,
  //         };
  //       })
  //     )
  //     .subscribe({
  //       next: ({ chart8a, chart8b }) => {
  //         this.chart8aData = {
  //           labels: ['Proposal Submitted', 'Nil Return', 'No Response'],
  //           datasets: [
  //             {
  //               data: [...chart8a],
  //               backgroundColor: ['#16A34A', '#F59E0B', '#64748B'],
  //               borderWidth: 2,
  //               borderColor: '#ffffff',
  //               hoverOffset: 6,
  //             },
  //           ],
  //         };
  //         this.chart8bData = {
  //           labels: ['MAKE', 'TDF'],
  //           datasets: [
  //             {
  //               label: 'Proposals generated (by seek)',
  //               backgroundColor: ['#2563EB', '#0D9488'],
  //               data: [...chart8b],
  //             },
  //           ],
  //         };
  //         this.cdr.detectChanges();
  //       },
  //       error: (err) => {
  //         console.error('Seek proposal charts:', err);
  //       },
  //     });
  // }

  // private fetchSeekProposalsMakeTdf(): Observable<{ make: any[]; tdf: any[] }> {
  //   const url = '/transaction/seek/proposal/';
  //   return this.api.getAPI(`${url}?scheme_id__in=47,16`).pipe(
  //     catchError(() => of(null)),
  //     switchMap((combined: any) => {
  //       const rows = this.normalizeSeekProposalList(combined);
  //       const sid = (r: any) =>
  //         Number(r.scheme_id ?? r.scheme?.id ?? r.schemeId ?? NaN);
  //       let make = rows.filter((r) => sid(r) === 47);
  //       let tdf = rows.filter((r) => sid(r) === 16);
  //       const tagged = make.length + tdf.length;
  //       if (rows.length > 0 && tagged > 0) {
  //         return of({ make, tdf });
  //       }
  //       return forkJoin({
  //         make: this.api
  //           .getAPI(`${url}?scheme_id=47`)
  //           .pipe(catchError(() => of(null))),
  //         tdf: this.api
  //           .getAPI(`${url}?scheme_id=16`)
  //           .pipe(catchError(() => of(null))),
  //       }).pipe(
  //         map((pack) => ({
  //           make: this.normalizeSeekProposalList(pack.make),
  //           tdf: this.normalizeSeekProposalList(pack.tdf),
  //         }))
  //       );
  //     })
  //   );
  // }

  /** Same row shape as MAKE/TDF getProposalData / loadSeekProposalAnalytics. */
  private metricsFromSeekProposalRow(item: any): {
    ps: number;
    nr: number;
    no: number;
    gen: number;
  } {
    const n = (x: any) => {
      const v = Number(x);
      return Number.isFinite(v) ? v : 0;
    };
    const totalDir = Array.isArray(item.directorate)
      ? item.directorate.length
      : Array.isArray(item.directorates)
        ? item.directorates.length
        : n(item.total_directorates ?? item.directorate_count);
    const ps = n(
      item.proposal_submitted_count ??
        item.proposal_submitted ??
        item.proposals_submitted
    );
    const nr = n(
      item.nil_return_count ??
        item.nil_return_submitted ??
        item.nil_return ??
        item.nil_returns
    );
    let noR: number | null = null;
    for (const k of [
      'no_response_received',
      'no_response_count',
      'no_response',
    ] as const) {
      const v = item[k];
      if (v != null && v !== '') {
        const x = Number(v);
        if (Number.isFinite(x)) noR = x;
        break;
      }
    }
    const no =
      noR !== null ? noR : Math.max(0, totalDir - ps - nr);
    const direct = item.total_proposals_created ?? item.total_proposals_generated;
    let gen =
      direct != null && direct !== '' && Number.isFinite(Number(direct))
        ? Number(direct)
        : n(item.make_i_count ?? item.make_i) +
          n(item.make_ii_count ?? item.make_ii) +
          n(item.make_iii_count ?? item.make_iii) +
          n(item.tdf_proposals ?? item.tdf_count);
    return { ps, nr, no, gen };
  }

  private normalizeSeekProposalList(res: any): any[] {
    if (res == null || typeof res !== 'object') return [];
    if (Array.isArray(res)) return res;
    let root = res;
    if (
      root.status === 1 &&
      root.data != null &&
      typeof root.data === 'object'
    ) {
      root = root.data;
    }
    if (Array.isArray(root)) return root;
    const tryArr = (x: any) => (Array.isArray(x) ? x : null);
    return (
      tryArr(root.results) ||
      tryArr(root.data) ||
      tryArr(root.seek_proposals) ||
      tryArr(root.proposals) ||
      []
    );
  }
}

